import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingScreen';
import { AppIcon, type AppIconName } from '@/components/AppIcon';
import {
    handleNotificationClick,
    type NotificationData,
    type NotificationType
} from '@/lib/notifications';

interface ApiNotification {
    id: string;
    type: string;
    title: string;
    body: string;
    data?: string;
    is_read: number;
    created_at: string;
}

export default function NotificationsPage() {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState<NotificationData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [unreadCount, setUnreadCount] = useState(0);

    const loadNotifications = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await api.getNotifications({ limit: 50 });
            const mapped = (res.notifications || []).map((n: ApiNotification): NotificationData => ({
                id: n.id,
                type: n.type as NotificationType,
                title: n.title,
                body: n.body,
                data: n.data ? JSON.parse(n.data) : undefined,
                isRead: n.is_read === 1,
                createdAt: n.created_at,
            }));
            setNotifications(mapped);
            setUnreadCount(res.unreadCount);
        } catch (error) {
            console.error('Failed to load notifications:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadNotifications();
    }, [loadNotifications]);

    const handleMarkAsRead = async (id: string) => {
        try {
            await api.markNotificationRead(id);
            setNotifications(prev =>
                prev.map(n => n.id === id ? { ...n, isRead: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Failed to mark as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await api.markAllNotificationsRead();
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };

    const handleClick = (notification: NotificationData) => {
        // 未読なら既読にする
        if (!notification.isRead) {
            handleMarkAsRead(notification.id);
        }

        // 通知タイプに応じたナビゲーション
        handleNotificationClick(notification);

        // デフォルトのナビゲーション
        switch (notification.type) {
            case 'instant_request':
            case 'settlement_complete':
                navigate('/settlements');
                break;
            case 'budget_alert':
                navigate('/budgets');
                break;
            default:
                break;
        }
    };

    const getNotificationIcon = (type: NotificationType): AppIconName => {
        switch (type) {
            case 'instant_request':
                return 'wallet';
            case 'settlement_complete':
                return 'check';
            case 'budget_alert':
                return 'warning';
            default:
                return 'bell';
        }
    };

    const cleanNotificationTitle = (title: string): string =>
        title.replace(/^[\p{Extended_Pictographic}\uFE0F\u200D\s]+/u, '');

    const formatDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'たった今';
        if (diffMins < 60) return `${diffMins}分前`;
        if (diffHours < 24) return `${diffHours}時間前`;
        if (diffDays < 7) return `${diffDays}日前`;

        return date.toLocaleDateString('ja-JP', {
            month: 'short',
            day: 'numeric',
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="pb-4">
            <div className="page-topbar">
                <div className="page-topbar-inner">
                    <h1 className="text-lg font-bold">通知</h1>
                    {unreadCount > 0 && (
                        <button
                            onClick={handleMarkAllAsRead}
                            className="text-sm text-primary-500 font-medium"
                        >
                            すべて既読にする
                        </button>
                    )}
                </div>
            </div>

            <div className="p-4">
                {notifications.length > 0 ? (
                    <div className="card divide-y divide-gray-100">
                        {notifications.map((notification) => (
                            <div
                                key={notification.id}
                                onClick={() => handleClick(notification)}
                                className={`p-4 cursor-pointer transition-colors ${!notification.isRead ? 'bg-primary-50' : ''
                                    }`}
                            >
                                <div className="flex items-start gap-3">
                                    <span
                                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-primary-600 flex-shrink-0"
                                        data-testid={`notification-icon-${notification.type}`}
                                    >
                                        <AppIcon name={getNotificationIcon(notification.type)} size={19} />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className={`font-medium ${!notification.isRead ? 'text-gray-900' : 'text-gray-600'}`}>
                                                {cleanNotificationTitle(notification.title)}
                                            </p>
                                            {!notification.isRead && (
                                                <span className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0" />
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                            {notification.body}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-2">
                                            {formatDate(notification.createdAt)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <div className="empty-icon mx-auto mb-3"><AppIcon name="bell" size={22} /></div>
                        <p className="text-gray-500">通知はありません</p>
                    </div>
                )}
            </div>
        </div>
    );
}
